/**
 * 商品规格分类 → 字段映射（唯一真理源）
 *
 * 消费方：PC 商品管理(ProductManage.vue) / 搜索页(Search.vue) / 移动端商品编辑(AdminProductEdit.vue)
 *
 * 字段的 tagType 对应后端 product_tags.tag_type（spec_* 前缀）；
 * 分类 key 对应 products.category。
 * 修改此处即三端同步，禁止在任何消费方重复硬编码。
 */

import { CATEGORY_DICTIONARY } from './category-dictionary'

// 分类 key → 规格字段定义
export const SPEC_FIELD_MAP = {
  tire: [
    { key: 'width', label: '胎宽', tagType: 'spec_width' },
    { key: 'size', label: '寸数', tagType: 'spec_size' },
    { key: 'ply', label: '层级(PR)', tagType: 'spec_ply' },
    { key: 'tire_type', label: '内/外胎', tagType: 'spec_tire_type' }
  ],
  battery: [
    { key: 'voltage', label: '电压', tagType: 'spec_voltage' },
    { key: 'batt_type', label: '类型', tagType: 'spec_batt_type' },
    { key: 'capacity', label: '容量', tagType: 'spec_batt_capacity' },
    { key: 'batt_model', label: '型号', tagType: 'spec_batt_model' }
  ],
  oil: [
    { key: 'grade', label: '粘度', tagType: 'spec_grade' }
  ],
  bearing: [
    { key: 'bearing_model', label: '型号', tagType: 'spec_bearing_model' },
    { key: 'bearing_id', label: '内径(mm)', tagType: 'spec_bearing_id' },
    { key: 'bearing_od', label: '外径(mm)', tagType: 'spec_bearing_od' },
    { key: 'bearing_width', label: '宽度(mm)', tagType: 'spec_bearing_width' },
    { key: 'bearing_type', label: '类型', tagType: 'spec_bearing_type' }
  ],
  seal: [
    { key: 'seal_id', label: '内径(mm)', tagType: 'spec_seal_id' },
    { key: 'seal_od', label: '外径(mm)', tagType: 'spec_seal_od' },
    { key: 'seal_thickness', label: '厚度(mm)', tagType: 'spec_seal_thickness' },
    { key: 'seal_material', label: '材质', tagType: 'spec_seal_material' }
  ],
  chain: [
    { key: 'chain_pitch', label: '节距', tagType: 'spec_chain_pitch' },
    { key: 'chain_links', label: '节数', tagType: 'spec_chain_links' }
  ],
  sprocket: [
    { key: 'sprocket_teeth', label: '齿数', tagType: 'spec_sprocket_teeth' },
    { key: 'sprocket_pitch', label: '节距', tagType: 'spec_sprocket_pitch' }
  ],
  spark_plug: [
    { key: 'spark_thread', label: '螺纹规格', tagType: 'spec_spark_thread' },
    { key: 'spark_heat', label: '热值', tagType: 'spec_spark_heat' },
    { key: 'spark_length', label: '螺纹长度', tagType: 'spec_spark_length' }
  ],
  belt: [
    { key: 'belt_model', label: '型号', tagType: 'spec_belt_model' },
    { key: 'belt_length', label: '长度(mm)', tagType: 'spec_belt_length' },
    { key: 'belt_width', label: '宽度(mm)', tagType: 'spec_belt_width' }
  ],
  bulb: [
    { key: 'bulb_model', label: '型号', tagType: 'spec_bulb_model' },
    { key: 'bulb_voltage', label: '电压(V)', tagType: 'spec_bulb_voltage' },
    { key: 'bulb_power', label: '功率(W)', tagType: 'spec_bulb_power' }
  ],
  brake_pad: [
    { key: 'brake_model', label: '型号', tagType: 'spec_brake_model' },
    { key: 'brake_material', label: '材质', tagType: 'spec_brake_material' }
  ],
  // 排气系统：排气管/排气垫/接口垫/消音相关件（车型沿用通用 tag，其余为本类专用）
  exhaust: [
    { key: 'vehicle_model', label: '适用车型', tagType: 'spec_vehicle_model' },
    { key: 'material', label: '材质', tagType: 'spec_exhaust_material' },
    { key: 'diameter', label: '接口口径', tagType: 'spec_exhaust_diameter' },
    { key: 'length', label: '长度', tagType: 'spec_exhaust_length' },
    { key: 'position', label: '安装位置', tagType: 'spec_exhaust_position' },
    { key: 'color', label: '颜色', tagType: 'spec_exhaust_color' }
  ],
  // 紧固件：螺丝/螺母/垫片/卡簧等
  fastener: [
    { key: 'thread', label: '螺纹规格', tagType: 'spec_fastener_thread' },
    { key: 'length', label: '长度', tagType: 'spec_fastener_length' },
    { key: 'material', label: '材质', tagType: 'spec_fastener_material' },
    { key: 'strength', label: '强度等级', tagType: 'spec_fastener_strength' },
    { key: 'head_type', label: '头型', tagType: 'spec_fastener_head_type' },
    { key: 'pitch', label: '螺距', tagType: 'spec_fastener_pitch' },
    { key: 'surface', label: '表面处理', tagType: 'spec_fastener_surface' }
  ],
  tool: [
    { key: 'tool_size', label: '尺寸', tagType: 'spec_tool_size' }
  ],
  // 通用配件类：唯一可比维度是车型适配码（行业内即型号），字段同一定义
  ...['adhesive', 'battery_part', 'bodywork', 'brake_pump', 'cable', 'camshaft', 'carburetor', 'cdi',
    'brake', 'charger', 'chemicals', 'clutch', 'coil', 'control', 'crankshaft', 'cylinder_head', 'cylinder_kit', 'filter', 'fuel_cap',
    'helmet', 'horn', 'ignition_coil', 'ignition_lock', 'lamp', 'lever', 'lock', 'luggage', 'meter', 'mirror', 'misc', 'oil_pump', 'piston',
    'protection', 'pulley', 'rectifier', 'regulator', 'relay', 'rim', 'rocker_arm', 'seat', 'shock_absorber',
    'starter_motor', 'switch', 'universal_joint', 'valve', 'variator'
  ].reduce((m, c) => { m[c] = [{ key: 'vehicle_model', label: '适配车型', tagType: 'spec_vehicle_model' }]; return m; }, {})
}

// 分类元数据（key → 中文名 + 图标），UI 展示用；
// 中文名：结构化类（有自定义字段）在此维护，其余类从 category-dictionary 取
// 图标优先级：pass 专属图 > 项目原有图标 > /icons/default.svg 兜底(当前52类全有图,暂无引用)
export const SPEC_CATEGORY_META = {
  tire: { name: '轮胎', icon: '/icons/tire-spec.svg' },
  battery: { name: '电池', icon: '/icons/battery-spec.svg' },
  oil: { name: '机油', icon: '/icons/oil-spec.svg' },
  bearing: { name: '轴承', icon: '/icons/bearing.svg' },
  seal: { name: '密封件', icon: '/icons/seal.svg' },
  chain: { name: '链条', icon: '/icons/chain.svg' },
  sprocket: { name: '链轮', icon: '/icons/sprocket.svg' },
  spark_plug: { name: '火花塞', icon: '/icons/spark-plug.svg' },
  belt: { name: '皮带', icon: '/icons/belt.svg' },
  bulb: { name: '灯泡', icon: '/icons/bulb.svg' },
  brake_pad: { name: '刹车片', icon: '/icons/brake-pad.svg' },
  // 排气系统专属画稿已入(pass/排气管.svg)
  exhaust: { name: '排气系统', icon: '/icons/exhaust.svg' },
  fastener: { name: '紧固件', icon: '/icons/fastener.svg' },
  tool: { name: '工具', icon: '/icons/tool.svg' },
  // 通用配件类图标（slug 文件名；缸头/机油泵 暂无任何图,default兜底）
  adhesive: { icon: '/icons/adhesive.svg' },
  battery_part: { icon: '/icons/battery-part.svg' },
  bodywork: { icon: '/icons/bodywork.svg' },
  brake_pump: { icon: '/icons/brake-pump.svg' },
  cable: { icon: '/icons/cable.svg' },
  camshaft: { icon: '/icons/camshaft.svg' },
  carburetor: { icon: '/icons/carburetor.svg' },
  cdi: { icon: '/icons/cdi.svg' },
  charger: { icon: '/icons/charger.svg' },
  clutch: { icon: '/icons/clutch.svg' },
  coil: { icon: '/icons/coil.svg' },
  crankshaft: { icon: '/icons/crankshaft.svg' },
  cylinder_head: { icon: '/icons/cylinder-head.svg' },
  cylinder_kit: { icon: '/icons/cylinder-kit.svg' },
  filter: { icon: '/icons/filter.svg' },
  fuel_cap: { icon: '/icons/fuel-cap.svg' },
  helmet: { icon: '/icons/helmet.svg' },
  horn: { icon: '/icons/horn.svg' },
  ignition_coil: { icon: '/icons/ignition-coil.svg' },
  ignition_lock: { icon: '/icons/ignition-lock.svg' },
  lever: { icon: '/icons/lever.svg' },
  lock: { icon: '/icons/lock.svg' },
  meter: { icon: '/icons/meter.svg' },
  misc: { icon: '/icons/misc.svg' },
  mirror: { icon: '/icons/mirror.svg' },
  oil_pump: { icon: '/icons/oil-pump.svg' },
  piston: { icon: '/icons/piston.svg' },
  pulley: { icon: '/icons/pulley.svg' },
  rectifier: { icon: '/icons/rectifier.svg' },
  regulator: { icon: '/icons/regulator.svg' },
  relay: { icon: '/icons/relay.svg' },
  rim: { icon: '/icons/rim.svg' },
  rocker_arm: { icon: '/icons/rocker-arm.svg' },
  seat: { icon: '/icons/seat.svg' },
  shock_absorber: { icon: '/icons/shock-absorber.svg' },
  starter_motor: { icon: '/icons/starter-motor.svg' },
  switch: { icon: '/icons/switch.svg' },
  universal_joint: { icon: '/icons/universal-joint.svg' },
  valve: { icon: '/icons/valve.svg' },
  variator: { icon: '/icons/variator.svg' },
  // Phase2 新建 5 类：暂无专属画稿,default 兜底(pass 出图后替换)
  lamp: { name: '车灯总成', icon: '/icons/lamp.svg' },
  control: { name: '操控件', icon: '/icons/control.svg' },
  chemicals: { name: '养护化学品', icon: '/icons/chemicals.svg' },
  luggage: { name: '尾箱', icon: '/icons/luggage.svg' },
  protection: { name: '防护用品', icon: '/icons/protection.svg' },
  // Phase3 新建,暂无画稿
  brake: { name: '制动系统', icon: '/icons/brake.svg' }
}

// 分类中文名：SPEC_CATEGORY_META 优先，否则查 51 类分类字典（唯一真理源），兜底 key
export function specCategoryName (key) {
  return (SPEC_CATEGORY_META[key] && SPEC_CATEGORY_META[key].name) ||
    (CATEGORY_DICTIONARY[key] && CATEGORY_DICTIONARY[key].name) || key
}

// 取某分类的规格字段（无则空数组）
export function getSpecFields (category) {
  return SPEC_FIELD_MAP[category] || []
}

// 分类选项（移动端/PC 分类选择器用）：[{value, label}]
export function buildCategoryOptions () {
  return Object.keys(SPEC_FIELD_MAP).map(key => ({
    value: key,
    label: specCategoryName(key)
  }))
}

// 搜索页分类数组（含 fields + options 占位，供 fetchFilters 填充）
export function buildSpecCategories () {
  return Object.keys(SPEC_FIELD_MAP).map(key => ({
    key,
    name: specCategoryName(key),
    icon: (SPEC_CATEGORY_META[key] && SPEC_CATEGORY_META[key].icon) || '',
    fields: SPEC_FIELD_MAP[key].map(f => ({ ...f, options: [] }))
  }))
}
